import type { ReactNode } from 'react';
import { ThemeProvider as MUIThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { useTranslation } from 'react-i18next';

type Props = {
  children: ReactNode;
};

export const ThemeProvider = ({ children }: Props) => {
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const theme = createTheme({
    direction: isRtl ? 'rtl' : 'ltr',
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1280,
        xl: 1920,
      },
    },
    palette: {
      primary: {
        main: '#0a66c2',
      },
      background: { default: '#fff', paper: '#f4f6f8' },
      text: {
        primary: '#1a1a1a',
        secondary: '#666666',
      },
      map: {
        beirut: '#FFE5E5',
        mountLebanon: '#E5F5FF',
        north: '#FFF5E5',
        bekaa: '#E5FFE5',
        south: '#F5E5FF',
        nabatieh: '#FFE5F5',
        akkar: '#E5FFFF',
        baalbekHermel: '#FFFFE5',
      },
      custom: {
        blue: {
          dark: '#003B73',
          darker: '#003679',
          medium: '#205EAB',
          regular: '#1a4a8a',
          icon: '#0C3E7C',
          text: '#00132b',
        },
        red: {
          main: '#AE0000',
        },
        grey: {
          light: '#fbfbfb',
          lighter: '#f8f9fa',
          neutral: '#e0e0e0',
          border: '#B1B1B1',
          borderLight: '#C8C8C8',
          background: '#E7ECF2',
          surface: '#F0F0F0',
          dark: '#333333',
        },
      },
    },
    typography: {
      fontFamily: '"Alexandria Variable", "Alexandria", sans-serif',
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: '0.5625rem',
            backgroundColor: 'rgba(244, 244, 244, 1)',
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          },
        },
      },
    },
  });

  const cache = createCache({
    key: isRtl ? 'muirtl' : 'mui',
    stylisPlugins: isRtl ? [prefixer, rtlPlugin] : [],
  });

  return (
    <CacheProvider value={cache}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </CacheProvider>
  );
};
